import { useAuth } from "@clerk/clerk-react";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { BentoContact, NewBentoContact } from "schemas/dashboard";
import { PreviewQuickSendBody } from "schemas/outreach";

import { fetcherAuth } from "utils/api";

import { OrganizationUserContext } from "./Organization";
import { LastContactedDate } from "./QuickSend";

interface QuickSendContactsInterface {
  getLastContacted: (bentoContactId: number) => LastContactedDate | undefined;
  updateLastContacted: (
    bentoContactIds: number[],
    override?: boolean,
  ) => Promise<LastContactedDate[]>;
  bentoContacts: BentoContact[];
  setBentoContacts: Dispatch<SetStateAction<BentoContact[]>>;
  getPreviewContacts: (
    brandId: number,
    data: PreviewQuickSendBody,
    bentoContact?: BentoContact | null,
    refetchContacts?: boolean,
  ) => Promise<{ contacts: BentoContact[]; data: PreviewQuickSendBody }>;
  updateBentoContacts: (
    selectableContacts: BentoContact[],
    bentoContact: BentoContact,
    resetBentoContacts: boolean,
  ) => void;
  fromMultipleCountries: boolean;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  getLastContacted: defaultContextMissingFunction,
  updateLastContacted: defaultContextMissingFunction,
  bentoContacts: [],
  setBentoContacts: defaultContextMissingFunction,
  getPreviewContacts: defaultContextMissingFunction,
  updateBentoContacts: defaultContextMissingFunction,
  fromMultipleCountries: false,
};

const QuickSendContactsContext =
  createContext<QuickSendContactsInterface>(defaultInterface);

interface Props {
  children: React.ReactNode;
}

const QuickSendContactsProvider = ({ children }: Props) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const bcid = searchParams.get("bcid");

  const [lastContactedMap, setLastContactedMap] = useState(
    new Map<number, LastContactedDate>(),
  );
  const [bentoContacts, setBentoContacts] = useState<BentoContact[]>([]);
  const [fromMultipleCountries, setFromMultipleCountries] =
    useState<boolean>(false);

  const getLastContacted = useCallback(
    (id: number) => {
      return lastContactedMap.get(id);
    },
    [lastContactedMap],
  );

  const updateLastContacted = async (
    bentoContactIds: number[],
    override: boolean = false,
  ): Promise<LastContactedDate[]> => {
    const idsToFetch = [];
    const existingResults: { [key in string]: LastContactedDate } = {};

    for (const bentoContactId of bentoContactIds) {
      const lastContacted = getLastContacted(bentoContactId);
      if (override || lastContacted === undefined) {
        idsToFetch.push(bentoContactId);
      } else {
        existingResults[bentoContactId] = lastContacted;
      }
    }

    const fetchedLastContacted = await fetchLastContactDate(idsToFetch);

    const finalResults: LastContactedDate[] = [];

    for (const id of bentoContactIds) {
      if (id in existingResults) {
        finalResults.push(existingResults[id]);
      } else {
        finalResults.push(fetchedLastContacted[id]);
      }
    }

    return finalResults;
  };

  const fetchLastContactDate = async (
    bentoContactIds: number[],
  ): Promise<{ [key in string]: LastContactedDate }> => {
    if (!bentoContactIds || bentoContactIds?.length === 0) {
      return {};
    }
    try {
      const queryString = bentoContactIds.map((id) => `ids=${id}`).join("&");
      const { lastContacted } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach/last-contacted?${queryString}`,
        "GET",
      );
      setLastContactedMap((prev) => {
        const updatedMap = new Map(prev);
        for (const id in lastContacted) {
          updatedMap.set(parseInt(id), lastContacted[id]);
        }
        for (const id of bentoContactIds) {
          if (!updatedMap.has(id)) {
            updatedMap.set(id, null);
          }
        }
        return updatedMap;
      });
      return lastContacted;
    } catch (error) {
      setLastContactedMap((prev) => {
        const updatedMap = new Map(prev);
        for (const id of bentoContactIds) {
          updatedMap.set(id, null);
        }
        return updatedMap;
      });
    }
    return {};
  };

  const getPreviewContacts = async (
    brandId: number,
    previewData: PreviewQuickSendBody,
    specificContact?: BentoContact | NewBentoContact | null,
    refetchContacts?: boolean,
  ) => {
    let data = { ...previewData };

    let contacts = bentoContacts;
    let bestContact = null;
    if (refetchContacts || bcid) {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-brands/${brandId}/contacts`,
        "GET",
      );
      contacts = res.selectableContacts;
      updateLastContacted(contacts?.map((x) => x.id!));
      bestContact = res.bestContact;
      setFromMultipleCountries(res.fromMultipleCountries);
    }
    // User click on a link from "Found New Contact" email

    const hasBcid = bcid && !isNaN(parseInt(bcid));
    const contactFromBcId = hasBcid
      ? contacts?.find((x) => x.id === Number(bcid))
      : undefined;

    if (contactFromBcId) {
      data.previewContact = contactFromBcId;
    } else if (specificContact?.email) {
      data.previewContact = specificContact;
    } else if (bestContact) {
      data.previewContact = bestContact;
    } else if (bentoContacts[0]?.bentoBrandId === Number(brandId)) {
      data.previewContact = bentoContacts[0];
    }
    if (hasBcid && !contactFromBcId) {
      searchParams.delete("bcid");
      setSearchParams(searchParams);
    }
    return { contacts, data };
  };

  const updateBentoContacts = (
    selectableContacts: BentoContact[],
    previewContact: BentoContact,
    resetBentoContacts: boolean,
  ) => {
    // If user add their own contact or from referred email,
    // update `selectableContacts` for this BentoBrand.
    const contacts = selectableContacts;
    const exists = contacts?.find(
      (x: BentoContact) => x?.email === previewContact?.email,
    );
    if (!exists && previewContact?.email) {
      contacts.push(previewContact);
      if (previewContact?.id) {
        updateLastContacted([previewContact?.id!]);
      }
    }
    if (resetBentoContacts || !exists) {
      setBentoContacts(contacts);
    }
  };

  return (
    <QuickSendContactsContext.Provider
      value={{
        getLastContacted,
        updateLastContacted,
        bentoContacts,
        setBentoContacts,
        getPreviewContacts,
        updateBentoContacts,
        fromMultipleCountries,
      }}
    >
      {children}
    </QuickSendContactsContext.Provider>
  );
};

export { QuickSendContactsProvider, QuickSendContactsContext };
